import React, { memo, useState, useEffect, useRef, Fragment } from 'react';
import styled from 'styled-components';
import { Wishlist, Icon } from 'tmt-ui-kit';
import range from 'lodash/range';

import ShareButton from '../ShareButton';
import { path } from '~/utils/media';
import { mergeCoverAndImages } from '~/utils/gallery';

const CoverImageContainer = styled.div`
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 650px;
  z-index: 999;
  background: #111;

  @media(max-width: 700px) {
    height: 210px;
  }
  @media(min-width: 701px) and (max-width: 1200px) {
    height: 430px;
  }
`;

const CoverImageInnerContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  max-width: 1168px;
  margin: 0 auto;
  background: #111;
`;

const CoverImageGradient = styled.div`
  position: absolute;
  top: 0;
  display: block;
  z-index: 2;
  width: 100%;
  height: 60px;
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0));
  transition: height 0.5s cubic-bezier(0.39, 0.575, 0.565, 1);
  @media(min-width: 1201px) {
    display: none;
  }
`;

const IconButtonContainer = styled.div`
  position: absolute;
  top: 4px;
  right: ${({ order = 0}) => {
    const space = (order * 40) + 16;
    return `${space}px`;
  }};
  z-index: 5;
  width: 40px;
  height: 40px;
  padding: 5px;
  box-sizing: border-box;
  @media(min-width: 1201px) {
    display: none;
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0 0;
`;

const Circle = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => (props.active ? props.theme.orange : props.theme.lightGray)};
  margin: 0 4px;
  cursor: pointer;
  &:hover {
    background-color: ${props => props.theme.orange};
  }
  &:first-child {
    margin: 0 4px 0 0;
  }
  &:last-child {
    margin: 0 0 0 4px;
  }
  @media(max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

const LeftMobileContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  user-select: none;
  @media(min-width: 1201px) {
    display: none;
  }
`;

const RightMobileContainer = styled(LeftMobileContainer)`
  left: unset;
  right: 0;
  @media(min-width: 1201px) {
    display: none;
  }
`;

const ChevronIcon = styled(Icon)`
  color: ${props => props.theme.white};
  font-size: 48px;
  width: 48px;
  height: 48px;
  font-weight: 600;
`;

const Images = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  user-select: none;
  cursor: pointer;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  transition: opacity 1s ease-in-out;
  opacity: ${props => (props.active ? 1 : 0)};
  display: block;
  /* width: 100%; */
  height: 100%;
  object-fit: cover;
`;

function useCarouselLikeHandler(imagesCount) {
  const [activeIndex, setActiveIndex] = useState(0);
  const interval = useRef();
  function createInterval() {
    return setInterval(() => {
      setActiveIndex((oldIndex) => {
        if (oldIndex + 1 === imagesCount) {
          return 0;
        }
        return oldIndex + 1;
      });
    }, 3000);
  }
  function onClickStep(idx) {
    setActiveIndex(idx);
    clearInterval(interval.current);
    interval.current = createInterval();
  }
  useEffect(() => {
    interval.current = createInterval();
    return () => clearInterval(interval.current);
  }, []);
  return [activeIndex, onClickStep];
}

const Cover = props => {
  const { trip, isWishlisted } = props;
  const images = useRef(
    mergeCoverAndImages(trip.images, trip.cover_image)
      .map(img => ({
        image: path(img.image, 'tripCoverNew'),
        caption: img.caption,
      }))
  );
  const [activeIndex, onClickStep] = useCarouselLikeHandler(images.current.length);
  function onClickGallery() {
    props.onClickGallery(activeIndex);
  }
  function onClickShare(e) {
    e.stopPropagation();
    if (props.onClickShare) props.onClickShare();
  }
  function onClickWishlist(e) {
    e.stopPropagation();
    if (props.onClickWishlist) props.onClickWishlist(e);
  }
  function onClickLeft() {
    onClickStep(activeIndex === 0 ? images.current.length - 1 : activeIndex - 1);
  }
  function onClickRight() {
    onClickStep(activeIndex === images.current.length - 1 ? 0 : activeIndex + 1);
  }
  return (
    <Fragment>
      <CoverImageContainer>
        <CoverImageInnerContainer>
          <Images onClick={onClickGallery}>
            {images.current.map((image, idx) => (
              <Image src={image.image} alt={image.caption} active={idx === activeIndex} />
            ))}
          </Images>
          <CoverImageGradient />
          <IconButtonContainer order="0" onClick={onClickWishlist}>
            <Wishlist wished={isWishlisted} />
          </IconButtonContainer>
          <IconButtonContainer order="1" onClick={onClickShare}>
            <ShareButton />
          </IconButtonContainer>
          <LeftMobileContainer onClick={onClickLeft}>
            <ChevronIcon name="chevron-left" />
          </LeftMobileContainer>
          <RightMobileContainer onClick={onClickRight}>
            <ChevronIcon name="chevron-right" />
          </RightMobileContainer>
        </CoverImageInnerContainer>
      </CoverImageContainer>
      <CarouselContainer>
        {range(0, images.current.length).map((num) => <Circle onClick={() => onClickStep(num)} active={num === activeIndex} />)}
      </CarouselContainer>
    </Fragment>
  );
};

export default memo(Cover, (prevProps, nextProps) => {
  if (prevProps.trip._id !== nextProps.trip._id) return false;
  if (prevProps.isWishlisted !== nextProps.isWishlisted) return false;
  return true;
});
